/**
 * useEntity Hook
 *
 * Fetches entity data via DESCRIBE query and loads prefix mappings
 * for URI shortening in the entity property table.
 */

import { useState, useEffect, useCallback } from 'react';
import { describeEntity } from '@services/sparql.service';
import { loadPrefixes, shortenUri, type PrefixMap } from '@utils/prefix.utils';
import type { EntityProperty } from '@/types';

interface UseEntityReturn {
  properties: EntityProperty[];
  prefixes: PrefixMap;
  loading: boolean;
  error: string | null;
  label: string | null;
  refetch: () => Promise<void>;
}

/**
 * Well-known label predicates in order of preference.
 */
const LABEL_PREDICATES = [
  'http://www.w3.org/2000/01/rdf-schema#label',
  'http://www.w3.org/2004/02/skos/core#prefLabel',
  'http://purl.org/dc/terms/title',
  'http://schema.org/name',
  'http://xmlns.com/foaf/0.1/name',
];

/**
 * Extract a human-readable label from entity properties.
 */
function extractLabel(properties: EntityProperty[], preferredLang = 'en'): string | null {
  for (const pred of LABEL_PREDICATES) {
    const prop = properties.find((p) => p.predicate === pred);
    if (!prop) continue;

    // Prefer the requested language, fall back to any literal
    const langMatch = prop.objects.find(
      (o) => o.type === 'literal' && o.lang === preferredLang
    );
    if (langMatch) return langMatch.value;

    const anyLiteral = prop.objects.find((o) => o.type === 'literal');
    if (anyLiteral) return anyLiteral.value;
  }

  return null;
}

/**
 * Hook for fetching and displaying entity data.
 *
 * @param uri - Full URI of the entity to describe
 * @returns { properties, prefixes, loading, error, label, refetch }
 */
export function useEntity(uri: string): UseEntityReturn {
  const [properties, setProperties] = useState<EntityProperty[]>([]);
  const [prefixes, setPrefixes] = useState<PrefixMap>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState<string | null>(null);

  const fetchEntity = useCallback(async (): Promise<void> => {
    if (!uri) return;

    setLoading(true);
    setError(null);

    try {
      const [entityProperties, prefixMap] = await Promise.all([
        describeEntity(uri),
        loadPrefixes(),
      ]);

      setProperties(entityProperties);
      setPrefixes(prefixMap);
      setLabel(extractLabel(entityProperties));
    } catch (err) {
      console.error('[useEntity] Failed to fetch entity:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [uri]);

  useEffect(() => {
    fetchEntity();
  }, [fetchEntity]);

  return {
    properties,
    prefixes,
    loading,
    error,
    label,
    refetch: fetchEntity,
  };
}

/**
 * Helper to shorten a URI using loaded prefixes.
 * Re-exported here for convenience in entity components.
 */
export { shortenUri };
